import { CircularProgress, IconButton, type SvgIconProps, type IconButtonProps, makeStyles } from '@material-ui/core';
import { Colors } from '@rentguru/commons-utils';
import { CSSProperties, forwardRef } from 'react';

import { BOX_SHADOW } from '../../styles';
import { stopPropagation } from '../../utils';
import { Tooltip } from '../basics';

const useStyles = makeStyles({
  disabledIcon: {
    opacity: 0.25,
    backgroundColor: Colors.CARNATION_RED,
    boxShadow: BOX_SHADOW,
  },
});

export interface CustomIconButtonProps extends IconButtonProps {
  href?: string;
  target?: string;
  iconStyle?: CSSProperties;
  disabledIconStyle?: CSSProperties;
  tooltipText?: string;
  disableTooltip?: boolean;
  useDisabledOpacity?: boolean;
}

export interface BaseCustomIconButtonProps extends CustomIconButtonProps {
  Icon: React.FC<SvgIconProps> | React.FC<React.SVGProps<SVGSVGElement>> | typeof CircularProgress;
}

// eslint-disable-next-line react/display-name
export const CustomIconButton = forwardRef<HTMLButtonElement, BaseCustomIconButtonProps>(
  (
    {
      disabled = false,
      disabledIconStyle,
      iconStyle,
      Icon,
      tooltipText,
      disableTooltip,
      useDisabledOpacity = false,
      ...rest
    },
    ref
  ) => {
    const classes = useStyles();
    const disabledStyle = { fill: Colors.SILVER, color: Colors.SILVER, ...disabledIconStyle };
    const iconButton = (
      <IconButton
        onMouseDown={stopPropagation}
        disabled={disabled}
        classes={{
          disabled: useDisabledOpacity ? classes.disabledIcon : undefined,
        }}
        ref={ref}
        {...rest}
      >
        <Icon style={disabled && !useDisabledOpacity ? disabledStyle : iconStyle} />
      </IconButton>
    );

    if (tooltipText && !disableTooltip) {
      return (
        <Tooltip title={tooltipText} arrow placement="bottom">
          {disabled ? <span style={{ display: 'inline-flex' }}>{iconButton}</span> : iconButton}
        </Tooltip>
      );
    }

    return iconButton;
  }
);
